import { OperationHours } from '../../../models/OperationHours.ts';
import { useForm } from '@mantine/form';
import { Button, Group, Stack, Switch, Text, TextInput } from '@mantine/core';
import { dayMap, formatAndValidateTime } from '../../../utils/DateUtil.ts';
import React, { useEffect, useState } from 'react';
import { IsOpenStatus } from '../../../models/OperationHours.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';

export interface MutateMaintenanceServiceOperationHoursFormValues {
  operationHours: OperationHours;
}

interface MutateMaintenanceServiceOperationHoursFormProps {
  initialValues: MutateMaintenanceServiceOperationHoursFormValues;
  onSubmit: (
    formValues: MutateMaintenanceServiceOperationHoursFormValues,
  ) => void;
  onValuesChange?: (
    formValues: MutateMaintenanceServiceOperationHoursFormValues,
  ) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateMaintenanceServiceOperationHoursForm({
  initialValues,
  onSubmit,
  onValuesChange,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateMaintenanceServiceOperationHoursFormProps) {
  const form = useForm<MutateMaintenanceServiceOperationHoursFormValues>({
    mode: 'controlled',
    initialValues: {
      operationHours: initialValues.operationHours,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      values.operationHours.weeklySchedule.forEach((day) => {
        if (day.isOpen !== 'OPEN') return;
        if (!day.timeSlots || day.timeSlots.length === 0) return;

        day.timeSlots.forEach((slot, slotIdx) => {
          const start = Number(slot.startTime.replaceAll(':', ''));
          const end = Number(slot.endTime.replaceAll(':', ''));

          if (start > end) {
            errors[
              `operationHours.weeklySchedule.${day.dayOfWeek}.timeSlots.${slotIdx}.endTime`
            ] = ' ';
          }
        });
      });

      return errors;
    },
  });

  const [timeErrors, setTimeErrors] = useState<
    Record<string, { start?: boolean; end?: boolean }>
  >({});

  const commitTime = (
    dayOfWeek: string,
    field: 'start' | 'end',
    raw: string,
  ) => {
    const { formatted, valid } = formatAndValidateTime(raw);
    const timeField = field === 'start' ? 'startTime' : 'endTime';
    if (valid) {
      const schedule = form.values.operationHours.weeklySchedule;
      const idx = schedule.findIndex((w) => w.dayOfWeek === dayOfWeek);
      if (idx !== -1) {
        form.setFieldValue(
          `operationHours.weeklySchedule.${idx}.timeSlots.0.${timeField}`,
          formatted,
        );
      }
      setTimeErrors((prev) => {
        const next = { ...prev[dayOfWeek] };
        delete next[field];
        return { ...prev, [dayOfWeek]: next };
      });
    } else {
      setTimeErrors((prev) => ({
        ...prev,
        [dayOfWeek]: { ...prev[dayOfWeek], [field]: true },
      }));
    }
  };

  useEffect(() => {
    onValuesChange?.(form.values);
  }, [form.values, onValuesChange]);

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const isAllOperationHoursIsOpenUnknown =
    form.values.operationHours?.weeklySchedule.length > 0 &&
    form.values.operationHours?.weeklySchedule.every(
      (w) => w.isOpen === ('UNKNOWN' as IsOpenStatus),
    );

  const { colorScheme } = useTheme();

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Group justify={'center'}>
          <Button
            variant={'outline'}
            radius={'xl'}
            color={'#DEE2E6'}
            onClick={() => {
              const updated = form.values.operationHours.weeklySchedule.map(
                (w) => ({
                  ...w,
                  isOpen: 'OPEN' as IsOpenStatus,
                  timeSlots: [
                    {
                      startTime: '00:00:00',
                      endTime: '23:59:59',
                    },
                  ],
                }),
              );

              form.setFieldValue('operationHours.weeklySchedule', updated);
            }}
            style={{
              width: 104,
              height: 28,
              fontSize: 14,
              fontWeight: 400,
              color: colorScheme === 'light' ? 'black' : 'white',
            }}
            styles={{
              section: { marginRight: 8 },
              root: { paddingInline: 8 },
            }}
          >
            24小時營業
          </Button>
          <Button
            variant={'outline'}
            radius={'xl'}
            color={'#DEE2E6'}
            onClick={() => {
              const updated = form.values.operationHours.weeklySchedule.map(
                (w) => {
                  const isWeekday =
                    w.dayOfWeek === 'MONDAY' ||
                    w.dayOfWeek === 'TUESDAY' ||
                    w.dayOfWeek === 'WEDNESDAY' ||
                    w.dayOfWeek === 'THURSDAY' ||
                    w.dayOfWeek === 'FRIDAY';

                  if (isWeekday) {
                    // 平日：開啟 & 設定 00:00~23:59
                    return {
                      ...w,
                      isOpen: 'OPEN' as IsOpenStatus,
                      timeSlots: [
                        {
                          startTime: '00:00:00',
                          endTime: '23:59:59',
                        },
                      ],
                    };
                  }

                  // 假日：關閉 & 重置 timeSlots
                  return {
                    ...w,
                    isOpen: 'CLOSED' as IsOpenStatus,
                    timeSlots: [],
                  };
                },
              );

              form.setFieldValue('operationHours.weeklySchedule', updated);
            }}
            style={{
              width: 104,
              height: 28,
              fontSize: 14,
              fontWeight: 400,
              color: colorScheme === 'light' ? 'black' : 'white',
            }}
            styles={{
              section: { marginRight: 8 },
              root: { paddingInline: 8 },
            }}
          >
            平日營業
          </Button>
          <Button
            variant={'outline'}
            radius={'xl'}
            color={'#DEE2E6'}
            onClick={() => {
              const updated = form.values.operationHours.weeklySchedule.map(
                (w) => {
                  const isWeekend =
                    w.dayOfWeek === 'SATURDAY' || w.dayOfWeek === 'SUNDAY';

                  if (isWeekend) {
                    // 假日：開啟 & 設定 00:00~23:59
                    return {
                      ...w,
                      isOpen: 'OPEN' as IsOpenStatus,
                      timeSlots: [
                        {
                          startTime: '00:00:00',
                          endTime: '23:59:59',
                        },
                      ],
                    };
                  }

                  // 平日：關閉 & 重置 timeSlots
                  return {
                    ...w,
                    isOpen: 'CLOSED' as IsOpenStatus,
                    timeSlots: [],
                  };
                },
              );

              form.setFieldValue('operationHours.weeklySchedule', updated);
            }}
            style={{
              width: 104,
              height: 28,
              fontSize: 14,
              fontWeight: 400,
              color: colorScheme === 'light' ? 'black' : 'white',
            }}
            styles={{
              section: { marginRight: 8 },
              root: { paddingInline: 8 },
            }}
          >
            假日營業
          </Button>
          <Button
            variant={'outline'}
            radius={'xl'}
            color={'#DEE2E6'}
            onClick={() => {
              const updated = form.values.operationHours.weeklySchedule.map(
                (w) => ({
                  ...w,
                  isOpen: 'UNKNOWN' as IsOpenStatus,
                  timeSlots: [],
                }),
              );

              form.setFieldValue('operationHours.weeklySchedule', updated);
            }}
            style={{
              width: 104,
              height: 28,
              fontSize: 14,
              fontWeight: 400,
              color: colorScheme === 'light' ? 'black' : 'white',
            }}
            styles={{
              section: { marginRight: 8 },
              root: { paddingInline: 8 },
            }}
          >
            營業時間未知
          </Button>
          <Button
            variant={'outline'}
            radius={'xl'}
            color={'#DEE2E6'}
            onClick={() => {
              const mondaySchedule =
                form.values.operationHours.weeklySchedule.find(
                  (w) => w.dayOfWeek === 'MONDAY',
                );

              if (!mondaySchedule) return;

              const updated = form.values.operationHours.weeklySchedule.map(
                (w) => {
                  if (w.dayOfWeek === 'MONDAY') return w;

                  return {
                    ...w,
                    isOpen: mondaySchedule.isOpen,
                    timeSlots: mondaySchedule.timeSlots.map((slot) => ({
                      ...slot,
                    })),
                  };
                },
              );

              form.setFieldValue('operationHours.weeklySchedule', updated);
            }}
            style={{
              width: 104,
              height: 28,
              fontSize: 14,
              fontWeight: 400,
              color: colorScheme === 'light' ? 'black' : 'white',
            }}
            styles={{
              section: { marginRight: 8 },
              root: { paddingInline: 8 },
            }}
          >
            套用週一設定
          </Button>
        </Group>

        <Stack gap={16} style={{ paddingTop: 24 }}>
          {isAllOperationHoursIsOpenUnknown ? (
            <div
              style={{
                backgroundColor:
                  colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                width: 627,
                height: 100,
                borderRadius: 4,
                padding: 16,
                color: 'grey',
                alignContent: 'center',
                justifyContent: 'center',
              }}
            >
              <Group justify={'center'}>
                <Text style={{ fontSize: 14 }}>營業時間未知</Text>
              </Group>
            </div>
          ) : (
            Object.entries(dayMap).map(([key, { label }]) => (
              <div
                key={key}
                style={{
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  width: 627,
                  height: 55,
                  borderRadius: 4,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Group align={'center'}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '22px',
                      color:
                        form.values.operationHours?.weeklySchedule.find(
                          (w) => w.dayOfWeek === key,
                        ).isOpen === 'OPEN'
                          ? colorScheme === 'light'
                            ? 'black'
                            : 'white'
                          : 'grey',
                    }}
                  >
                    {label}
                  </div>
                  {form.values.operationHours?.weeklySchedule
                    .find((w) => w.dayOfWeek === key)
                    ?.isOpen.toString() === 'OPEN' && (
                    <>
                      <TextInput
                        value={
                          form.values.operationHours?.weeklySchedule
                            .find((w) => w.dayOfWeek === key)
                            ?.timeSlots[0]?.startTime.split(':')
                            .slice(0, 2)
                            .join(':') ?? '00:00'
                        }
                        onChange={(e) => {
                          const schedule =
                            form.values.operationHours.weeklySchedule;
                          const idx = schedule.findIndex(
                            (w) => w.dayOfWeek === key,
                          );
                          if (idx === -1) return;
                          form.setFieldValue(
                            `operationHours.weeklySchedule.${idx}.timeSlots.0.startTime`,
                            e.target.value,
                          );
                        }}
                        onBlur={(e) => commitTime(key, 'start', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitTime(key, 'start', e.currentTarget.value);
                          }
                        }}
                        error={!!timeErrors[key]?.start}
                        placeholder="HHMM"
                        size="xs"
                        style={{ width: 60, fontSize: 12, fontWeight: 400 }}
                        styles={{
                          input: { fontSize: 12, textAlign: 'center' },
                        }}
                      />
                      <div> - </div>
                      <TextInput
                        value={
                          form.values.operationHours?.weeklySchedule
                            .find((w) => w.dayOfWeek === key)
                            ?.timeSlots[0]?.endTime.split(':')
                            .slice(0, 2)
                            .join(':') ?? '23:59'
                        }
                        onChange={(e) => {
                          const schedule =
                            form.values.operationHours.weeklySchedule;
                          const idx = schedule.findIndex(
                            (w) => w.dayOfWeek === key,
                          );
                          if (idx === -1) return;
                          form.setFieldValue(
                            `operationHours.weeklySchedule.${idx}.timeSlots.0.endTime`,
                            e.target.value,
                          );
                        }}
                        onBlur={(e) => commitTime(key, 'end', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitTime(key, 'end', e.currentTarget.value);
                          }
                        }}
                        error={
                          !!timeErrors[key]?.end ||
                          !!form.errors[
                            `operationHours.weeklySchedule.${key}.timeSlots.0.endTime`
                          ]
                        }
                        placeholder="HHMM"
                        size="xs"
                        style={{ width: 60, fontSize: 12, fontWeight: 400 }}
                        styles={{
                          input: { fontSize: 12, textAlign: 'center' },
                        }}
                      />
                    </>
                  )}
                </Group>

                <div style={{ height: 24, alignContent: 'center' }}>
                  <Switch
                    checked={
                      form.values.operationHours?.weeklySchedule.find(
                        (w) => w.dayOfWeek === key,
                      )?.isOpen === 'OPEN'
                    }
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;

                      const idx =
                        form.values.operationHours.weeklySchedule.findIndex(
                          (w) => w.dayOfWeek === key,
                        );

                      if (idx === -1) return;

                      if (!checked) {
                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.isOpen`,
                          'CLOSED',
                        );

                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.timeSlots.0.startTime`,
                          '00:00:00',
                        );

                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.timeSlots.0.endTime`,
                          '23:59:00',
                        );
                      } else {
                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.isOpen`,
                          'OPEN',
                        );

                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.timeSlots.0.startTime`,
                          '00:00:00',
                        );

                        form.setFieldValue(
                          `operationHours.weeklySchedule.${idx}.timeSlots.0.endTime`,
                          '23:59:00',
                        );
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </Stack>

        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </form>
    </>
  );
}
